import React from 'react';
import { Users, Shield, Scale, FileText, CheckCircle, AlertTriangle, XCircle, RotateCw, Clock, Upload, Eye, Download, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import AdvancedSearchBar from './AdvancedSearchBar';

export default function GEDESSPanels(ctx) {
  const {
    activePanel,
    connectedSearch, setConnectedSearch, connectedLoading, connectedData,
    membresFilters, setMembresFilters,
    ftpeFilters, setFtpeFilters,
    simFilters, setSimFilters,
    avenantSearch, setAvenantSearch, avenantFilters, setAvenantFilters,
    complianceData, loadCompliance,
    auditSearch, setAuditSearch, auditFilters, setAuditFilters, auditEntries,
    formatDate,
  } = ctx;

  return (
    <>
      {/* PANEL: Membres */}
      {activePanel === 'membres' && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-white pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-violet-600"><Users className="w-5 h-5 text-white" /></div>
              <div><Badge className="text-[10px] text-white mb-0.5 bg-violet-600 font-semibold">MEMBRES</Badge><CardTitle className="text-base">Tableau des membres</CardTitle></div>
            </div>
            <div className="mt-3">
              <AdvancedSearchBar
                searchQuery={connectedSearch}
                setSearchQuery={setConnectedSearch}
                filters={membresFilters}
                setFilters={setMembresFilters}
                categories={[
                  { value: 'actif', label: 'Actif' },
                  { value: 'en_attente', label: 'En attente' },
                  { value: 'incomplete', label: 'Incomplet' }
                ]}
                placeholder="Rechercher par nom, prenom, entreprise, email..."
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {connectedLoading ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="membres-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Nom</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Prenom</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Entreprise</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Espace</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Email</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Statut</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">FTPE</th>
                  </tr></thead>
                  <tbody>
                    {(connectedData.membres || [])
                      .filter(m => {
                        if (connectedSearch && ![m.nom, m.prenom, m.email, m.entreprise, m.espace].some(v => (v || '').toLowerCase().includes(connectedSearch.toLowerCase()))) return false;
                        if (membresFilters.categorie && m.status !== membresFilters.categorie) return false;
                        if (membresFilters.statut === 'ftpe_oui' && !m.is_ftpe) return false;
                        if (membresFilters.statut === 'ftpe_non' && m.is_ftpe) return false;
                        return true;
                      })
                      .map((m, i) => (
                      <tr key={m.id || i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`membre-row-${m.id}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{m.nom || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{m.prenom || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{m.entreprise || '-'}</td>
                        <td className="px-4 py-2.5"><Badge variant="outline" className="text-[10px]">{m.espace || '-'}</Badge></td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{m.email || '-'}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={cn("text-[10px]", m.status === 'actif' ? 'bg-green-100 text-green-700' : m.status === 'en_attente' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600')}>{m.status || '-'}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          {m.is_ftpe ? <Badge className="text-[10px] bg-orange-600 text-white font-semibold">Adherent</Badge> : <span className="text-xs text-gray-400">Non</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PANEL: FTPE Adhesions */}
      {activePanel === 'ftpe' && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-white pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-600"><Shield className="w-5 h-5 text-white" /></div>
              <div><Badge className="text-[10px] text-white mb-0.5 bg-orange-600 font-semibold">FTPE</Badge><CardTitle className="text-base">Suivi des adhesions FTPE</CardTitle></div>
            </div>
            <div className="mt-3">
              <AdvancedSearchBar
                searchQuery={connectedSearch}
                setSearchQuery={setConnectedSearch}
                filters={ftpeFilters}
                setFilters={setFtpeFilters}
                categories={[
                  { value: 'active', label: 'Active' },
                  { value: 'signed', label: 'Signee' },
                  { value: 'pending', label: 'En attente' }
                ]}
                placeholder="Rechercher par representant, raison sociale, email..."
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {connectedLoading ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto" /></div>
            ) : (connectedData.ftpe_adhesions || []).length === 0 ? (
              <div className="p-12 text-center"><Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-gray-500">Aucune adhesion FTPE enregistree</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ftpe-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Representant</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Raison sociale</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Email</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Statut</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Paiement</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Date</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Expiration</th>
                  </tr></thead>
                  <tbody>
                    {(connectedData.ftpe_adhesions || [])
                      .filter(a => {
                        if (connectedSearch && ![a.representant_nom, a.raison_sociale, a.email].some(v => (v || '').toLowerCase().includes(connectedSearch.toLowerCase()))) return false;
                        if (ftpeFilters.categorie && a.status !== ftpeFilters.categorie) return false;
                        return true;
                      })
                      .map((a, i) => (
                      <tr key={a.id || i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`ftpe-row-${a.id}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{a.representant_nom || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{a.raison_sociale || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{a.email || '-'}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={cn("text-[10px]", a.status === 'active' ? 'bg-green-100 text-green-700' : a.status === 'signed' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700')}>{a.status}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className={cn("text-[10px]", a.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>{a.payment_status || 'en attente'}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(a.created_at)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(a.expiration_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PANEL: Simulations RCDS */}
      {activePanel === 'simulations' && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-white pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-600"><Scale className="w-5 h-5 text-white" /></div>
              <div><Badge className="text-[10px] text-white mb-0.5 bg-blue-600 font-semibold">RCDS</Badge><CardTitle className="text-base">Simulations RCDS – Nominative, PIB, Avenants</CardTitle></div>
            </div>
            <div className="mt-3">
              <AdvancedSearchBar
                searchQuery={connectedSearch}
                setSearchQuery={setConnectedSearch}
                filters={simFilters}
                setFilters={setSimFilters}
                categories={[
                  { value: 'nominative', label: 'Nominative' },
                  { value: 'pib', label: 'PIB' },
                  { value: 'annuel', label: 'Annuel' }
                ]}
                placeholder="Rechercher par entreprise, representant, type..."
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {connectedLoading ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="simulations-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Entreprise</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Representant</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Type</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Statut</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-600">Montant Global</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Date</th>
                  </tr></thead>
                  <tbody>
                    {(connectedData.simulations_rcds || [])
                      .filter(s => {
                        if (connectedSearch && ![s.entreprise, s.nom_complet, s.type].some(v => (v || '').toLowerCase().includes(connectedSearch.toLowerCase()))) return false;
                        if (simFilters.categorie && !(s.type || '').toLowerCase().includes(simFilters.categorie)) return false;
                        return true;
                      })
                      .map((s, i) => (
                      <tr key={s.id || i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`simulation-row-${s.id}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{s.entreprise || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{s.nom_complet || '-'}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className={cn("text-[10px]",
                            (s.type || '').toLowerCase().includes('nominat') ? 'border-purple-300 text-purple-700' :
                            (s.type || '').toLowerCase().includes('pib') ? 'border-blue-300 text-blue-700' :
                            'border-gray-300 text-gray-600'
                          )}>{s.type || '-'}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className={cn("text-[10px]",
                            ['validee', 'active', 'PAYE'].includes(s.statut) ? 'bg-green-100 text-green-700' :
                            ['signee', 'DOCUMENTS_GENERES'].includes(s.statut) ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          )}>{s.statut || '-'}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium">{s.montant_ttc ? `${Number(s.montant_ttc).toLocaleString('fr-FR')} €` : '-'}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PANEL: Avenants */}
      {activePanel === 'avenants' && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-white pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-teal-600"><FileText className="w-5 h-5 text-white" /></div>
              <div><Badge className="text-[10px] text-white mb-0.5 bg-teal-600 font-semibold">AVENANTS</Badge><CardTitle className="text-base">Avenants RCDS</CardTitle></div>
            </div>
            <div className="mt-3">
              <AdvancedSearchBar searchQuery={avenantSearch} setSearchQuery={setAvenantSearch} filters={avenantFilters} setFilters={setAvenantFilters}
                categories={[{ value: 'completed', label: 'Complete' }, { value: 'pending', label: 'En attente' }]}
                placeholder="Rechercher par membre, type avenant, montant..." />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {connectedLoading ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto" /></div>
            ) : (connectedData.avenants || []).length === 0 ? (
              <div className="p-12 text-center"><FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-gray-500">Aucun avenant enregistre</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="avenants-table">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Membre</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Type</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Statut</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-600">Montant initial</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-gray-600">Montant final</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">Date</th>
                  </tr></thead>
                  <tbody>
                    {(connectedData.avenants || [])
                      .filter(av => {
                        if (avenantSearch && ![av.member_name, av.type_avenant].some(v => (v || '').toLowerCase().includes(avenantSearch.toLowerCase()))) return false;
                        if (avenantFilters.categorie && av.status !== avenantFilters.categorie) return false;
                        return true;
                      })
                      .map((av, i) => (
                      <tr key={av.id || i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`avenant-row-${av.id}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{av.member_name || '-'}</td>
                        <td className="px-4 py-2.5"><Badge variant="outline" className="text-[10px]">{av.type_avenant || '-'}</Badge></td>
                        <td className="px-4 py-2.5">
                          <Badge className={cn("text-[10px]", av.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>{av.status || '-'}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right">{av.montant_initial ? `${Number(av.montant_initial).toLocaleString('fr-FR')} €` : '-'}</td>
                        <td className="px-4 py-2.5 text-right font-medium">{av.montant_final ? `${Number(av.montant_final).toLocaleString('fr-FR')} €` : '-'}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(av.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PANEL: Conformite ESS */}
      {activePanel === 'conformite' && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-white pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-700"><CheckCircle className="w-5 h-5 text-white" /></div>
                <div>
                  <Badge className={cn("text-[10px] text-white mb-0.5", complianceData?.overall_status === 'conforme' ? 'bg-emerald-600' : 'bg-red-600')}>
                    {complianceData?.overall_status === 'conforme' ? 'CONFORME' : 'NON CONFORME'}
                  </Badge>
                  <CardTitle className="text-base">Conformite Workflow ESS</CardTitle>
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={loadCompliance} data-testid="refresh-compliance">
                <RotateCw className="w-3 h-3" /> Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {!complianceData ? (
              <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto" /></div>
            ) : (
              <div className="space-y-3" data-testid="compliance-checks">
                {complianceData.checks?.map((check, i) => (
                  <div key={i} className={cn("p-4 rounded-lg border-l-4 flex items-start gap-3",
                    check.status === 'conforme' ? 'border-l-emerald-500 bg-emerald-50/50' :
                    check.status === 'attention' ? 'border-l-amber-500 bg-amber-50/50' :
                    'border-l-red-500 bg-red-50/50'
                  )} data-testid={`compliance-${check.rule}`}>
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      check.status === 'conforme' ? 'bg-emerald-100' : check.status === 'attention' ? 'bg-amber-100' : 'bg-red-100'
                    )}>
                      {check.status === 'conforme' ? <CheckCircle className="w-4 h-4 text-emerald-600" /> :
                       check.status === 'attention' ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
                       <XCircle className="w-4 h-4 text-red-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900">{check.label}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{check.detail}</p>
                    </div>
                    <Badge className={cn("text-[10px] flex-shrink-0",
                      check.status === 'conforme' ? 'bg-emerald-100 text-emerald-700' :
                      check.status === 'attention' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    )}>{check.status}</Badge>
                  </div>
                ))}
                <div className="text-xs text-gray-500 text-right mt-2">
                  Verifie le {complianceData.checked_at ? new Date(complianceData.checked_at).toLocaleString('fr-FR') : '-'}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PANEL: Journal d'audit */}
      {activePanel === 'journal' && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-white pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-800"><Clock className="w-5 h-5 text-white" /></div>
              <div><Badge className="text-[10px] text-white mb-0.5 bg-gray-800 font-semibold">AUDIT</Badge><CardTitle className="text-base">Journal d'audit - Horodatage des interactions</CardTitle></div>
            </div>
            <div className="mt-3">
              <AdvancedSearchBar searchQuery={auditSearch} setSearchQuery={setAuditSearch} filters={auditFilters} setFilters={setAuditFilters}
                categories={[
                  { value: 'upload', label: 'Upload' }, { value: 'view', label: 'Consultation' },
                  { value: 'download', label: 'Telechargement' }, { value: 'delete', label: 'Suppression' },
                  { value: 'form_submission', label: 'Formulaire' }, { value: 'souscription', label: 'Souscription' },
                  { value: 'contact', label: 'Contact' }, { value: 'partenariat', label: 'Partenariat' }
                ]}
                placeholder="Rechercher par action, utilisateur, entite..." />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {auditEntries.length === 0 ? (
              <div className="p-12 text-center"><Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-gray-500">Aucune entree dans le journal</p><p className="text-xs text-gray-400 mt-1">Les interactions seront enregistrees automatiquement</p></div>
            ) : (
              <div className="divide-y max-h-[600px] overflow-y-auto" data-testid="audit-entries">
                {auditEntries
                  .filter(e => {
                    if (auditSearch && ![e.action_label, e.user_nom, e.user_prenom, e.user_email, e.entity].some(v => (v || '').toLowerCase().includes(auditSearch.toLowerCase()))) return false;
                    if (auditFilters.categorie && e.action_type !== auditFilters.categorie) return false;
                    return true;
                  })
                  .map((entry, i) => (
                  <div key={entry.id || i} className="px-4 py-3 hover:bg-gray-50/50 flex items-start gap-3" data-testid={`audit-entry-${entry.id}`}>
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5",
                      entry.action_type === 'upload' ? 'bg-emerald-100' :
                      entry.action_type === 'view' ? 'bg-blue-100' :
                      entry.action_type === 'download' ? 'bg-amber-100' :
                      entry.action_type === 'delete' ? 'bg-red-100' :
                      entry.action_type === 'form_submission' ? 'bg-purple-100' :
                      'bg-gray-100'
                    )}>
                      {entry.action_type === 'upload' ? <Upload className="w-3.5 h-3.5 text-emerald-600" /> :
                       entry.action_type === 'view' ? <Eye className="w-3.5 h-3.5 text-blue-600" /> :
                       entry.action_type === 'download' ? <Download className="w-3.5 h-3.5 text-amber-600" /> :
                       entry.action_type === 'delete' ? <Trash2 className="w-3.5 h-3.5 text-red-600" /> :
                       <Clock className="w-3.5 h-3.5 text-gray-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 font-medium">{entry.action_label}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-gray-500">
                        {(entry.user_prenom || entry.user_nom) && (
                          <span className="font-medium text-gray-700">{[entry.user_prenom, entry.user_nom].filter(Boolean).join(' ')}</span>
                        )}
                        {entry.user_email && <span>{entry.user_email}</span>}
                        {entry.user_entreprise && <Badge variant="outline" className="text-[10px]">{entry.user_entreprise}</Badge>}
                        {entry.entity && <Badge className="text-[10px] bg-gray-700 text-white font-semibold">{entry.entity}</Badge>}
                        <Badge variant="outline" className="text-[10px]">{entry.source}</Badge>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium text-gray-700">{entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('fr-FR') : ''}</p>
                      <p className="text-[10px] text-gray-500">{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString('fr-FR') : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
